import { isStaircaseDefinitionId } from "../furniture/C1_staircase";
import { eloSelectionOpacity, eloSelectionHoverHandlers } from "./C1_eloSelectionFocus";
import { panUnselectedPlanObject } from "./C1_planObjectPan";
import { C1_FurnitureSvgRenderer } from "../furniture/C1_FurnitureRenderer";
import {
  furnitureById,
  isElectricalFurnitureDefinition,
  isPureElectricalFurnitureDefinition,
} from "../furniture/C1_furnitureRegistry";
import type { C1_Floor } from "../model/C1_Floor";
import c1_state from "../model/C1_State";
import C1_EloItem from "../model/C1_EloItem";
import { C1_Consumer } from "../model/C1_Consumer";
import type { Rect } from "../types/C1_AppTypes";
import { clientToPlan } from "./C1_planCoordinates";

type FurniturePreview = {
  roomId: string;
  furnitureIndex: number;
  x: number;
  y: number;
  rotation?: number;
  wallDocked?: "L" | "R" | "M";
  reflect?: boolean;
  width?: number;
  height?: number;
};

function pointerInModel(event: React.PointerEvent, view: Rect) {
  const canvas = event.currentTarget.closest(".C1_canvas");
  return clientToPlan(canvas, event.clientX, event.clientY, view);
}

export { furnitureBounds } from "../lib/C1_furnitureBounds";
import { furnitureBounds } from "../lib/C1_furnitureBounds";

export function C1_FurnitureSvgView({
  floor,
  preview,
  onStartResize,
  onObjectDoubleClick,
}: {
  floor: C1_Floor;
  preview?: FurniturePreview;
  onStartResize?: (
    event: React.PointerEvent,
    roomId: string,
    furnitureIndex: number,
    axis: "width" | "height",
    edge: "start" | "end",
  ) => void;
  onObjectDoubleClick?: (bounds: Rect, clientPoint: { x: number; y: number }) => void;
}) {
  if (!floor) return null;

  const view = c1_state.view;
  const PX = c1_state.svgPx;
  const selection = c1_state.selection;
  const renderingPdf = c1_state.floorImageRenderOptions.value !== null;
  let selectedEloOwner = selection instanceof C1_EloItem ? selection.parent : undefined;
  while (selectedEloOwner instanceof C1_EloItem) selectedEloOwner = selectedEloOwner.parent;

  return (
    <g data-c1-svg-layer="furniture">
      {floor.rooms.flatMap((room) => {
        const placementRect = room.innerRect();
        return room.furnitures.map((furniture, furnitureIndex) => {
          const definition = furnitureById[furniture.definitionId];
          if (
            (c1_state.editMode.value === "furnishing" || c1_state.editMode.value === "building") &&
            definition &&
            isPureElectricalFurnitureDefinition(definition)
          )
            return null;
          const itemPreview =
            preview?.roomId === room.id && preview.furnitureIndex === furnitureIndex ? preview : undefined;
          const relativePosition = itemPreview ? { x: itemPreview.x, y: itemPreview.y } : furniture.relativePosition;
          const equipmentOrigin = itemPreview ? undefined : c1_state.project.equipmentPostitionGeometric(furniture.id);
          const modelX =
            equipmentOrigin?.x ?? placementRect.x1 + relativePosition.x * (placementRect.x2 - placementRect.x1);
          const modelY =
            equipmentOrigin?.y ?? placementRect.y1 + relativePosition.y * (placementRect.y2 - placementRect.y1);
          const isSelected = !renderingPdf && (selection === furniture ||
            (selection instanceof C1_Consumer && selection.lightGroup === furniture));
          const indirectlySelected =
            !renderingPdf &&
            selectedEloOwner?.className === furniture.className &&
            selectedEloOwner.id === furniture.id;
          const baseProps = {
            ...furniture.baseProps,
            ...(itemPreview?.rotation !== undefined ? { rotation: itemPreview.rotation } : {}),
            ...(itemPreview?.width !== undefined ? { width: itemPreview.width } : {}),
            ...(itemPreview?.height !== undefined ? { height: itemPreview.height } : {}),
          };

          return (
            <g key={furniture.id} data-c1-object-id={furniture.id} data-c1-room-id={room.id} opacity={eloSelectionOpacity(furniture, c1_state)} {...eloSelectionHoverHandlers(furniture, c1_state)}>
              <C1_FurnitureSvgRenderer
                x={(modelX - view.x1) * PX}
                y={(modelY - view.y1) * PX}
                PX={PX}
                isSelected={isSelected}
                indirectlySelected={indirectlySelected}
                furniture={{
                  ...furniture.toJSON(),
                  id: furniture.id,
                  eloTree: furniture.eloTree,
                  relativePosition,
                  wallDocked: itemPreview?.wallDocked ?? furniture.wallDocked,
                  reflect: itemPreview?.reflect ?? furniture.reflect,
                  baseProps,
                }}
                onStartResize={(event, axis, edge) => onStartResize?.(event, room.id, furnitureIndex, axis, edge)}
                onSetDimension={(axis, value) => {
                  furniture.setBaseProps({ ...furniture.baseProps, [axis]: value });
                  state.undoManager.userInteractionEnded();
                }}
                onDoubleClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  const originXRatio = (definition?.originXPercentage ?? 0) / 100;
                  const originYRatio = (definition?.originYPercentage ?? 0) / 100;
                  onObjectDoubleClick?.(
                    furnitureBounds(
                      modelX,
                      modelY,
                      baseProps.width,
                      baseProps.height,
                      Number(baseProps.rotation) || 0,
                      originXRatio,
                      originYRatio,
                    ),
                    { x: event.clientX, y: event.clientY },
                  );
                }}
                onPointerDown={(event) => {
                  const buildingFurniture = isStaircaseDefinitionId(furniture.definitionId);
                  const electricalMode = c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail";
                  const electricalFurniture = isElectricalFurnitureDefinition(definition);
                  const directElectricalDrag = electricalMode && electricalFurniture;
                  const furnitureSelected = isSelected || (indirectlySelected && electricalFurniture);
                  if (!furnitureSelected && !directElectricalDrag && panUnselectedPlanObject(event, () => {
                    c1_state.setSelection(c1_state.editMode.value === "building" && !buildingFurniture ? room : furniture);
                  })) return;
                  if (c1_state.editMode.value === "building" && !buildingFurniture) {
                    event.stopPropagation();
                    c1_state.setSelection(room);
                    return;
                  }
                  event.stopPropagation();
                  c1_state.setSelection(furniture);
                  if (
                    (electricalMode && !electricalFurniture) ||
                    c1_state.editMode.value === "interactionConcept" ||
                    (!furnitureSelected && !directElectricalDrag && c1_state.settings.selectObjectsBeforeMoving)
                  )
                    return;

                  event.currentTarget.setPointerCapture(event.pointerId);
                  const currentView = c1_state.viewFromBounds();
                  const pointer = pointerInModel(event, currentView);
                  if (!pointer) return;
                  c1_state.dragView.value = currentView;
                  c1_state.drag.value = {
                    type: "furniture",
                    roomId: room.id,
                    furnitureIndex,
                    startX: pointer.x,
                    startY: pointer.y,
                    startRelativePosition: { ...furniture.relativePosition },
                    roomRect: placementRect,
                    furnitureSize: {
                      width: furniture.baseProps.width,
                      height: furniture.baseProps.height,
                    },
                    startSide: furniture.side,
                    startReflect: furniture.reflect,
                    view: currentView,
                  };
                }}
              />
            </g>
          );
        });
      })}
    </g>
  );
}
